import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import moment from "moment";
import ReactPaginate from "react-paginate";
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import PptxGenJS from "pptxgenjs";

import Sidebar from "../sidebar";
import {
  deletePersonalizedUser,
  getPersonalizedUsers,
  getProductsByUuid,
  getUsers,
} from "./personalized-users-section.service";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import FormSelect from "../../../components/FormSelect";
import Toggle from "react-toggle";
import Swal from "sweetalert2";

export default function PersonalizedUsersSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [createdDate, setCreatedDate] = useState(moment().format("YYYY-MM-DD"));
  const [expireDate, setExpireDate] = useState(
    moment().add(1, "month").format("YYYY-MM-DD")
  );
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [users, setUsers] = useState([]);
  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get("page");
  const [currentPage, setCurrentPage] = useState(
    pageParam ? parseInt(pageParam) : 1
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    getUsers({
      limit: 500,
    }).then((response) => {
      setUsers(response.data.results);
    });
  }, []);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
    }
  }, [pageParam]);

  const {
    isPending,
    isError,
    data: response,
    error,
  } = useQuery({
    queryKey: ["admin-personalized-users", currentPage],
    queryFn: () => getPersonalizedUsers({ page: currentPage }),
    placeholderData: keepPreviousData,
  });

  const handlePageChange = (selectedItem: { selected: number }) => {
    const newPage = selectedItem.selected + 1;
    setCurrentPage(newPage);
    navigate(`?page=${newPage}`);
  };

  const handleDownloadPPT = async (user: any) => {
    const response = await getProductsByUuid(user.uuid);
    const pptx = new PptxGenJS();

    // Slide 1: Title Slide
    let slide1 = pptx.addSlide();
    slide1.background = { fill: "FFFFFF" }; // White background
    slide1.addText("Prince Art Exporter", {
      x: 1.5,
      y: 1,
      fontSize: 36,
      bold: true,
      color: "000000",
      align: "center",
    });
    slide1.addText("Photo Offer", {
      x: 1.5,
      y: 2,
      fontSize: 24,
      color: "666666",
      align: "center",
    });

    const products: any = response.data;

    for (let index = 0; index < products.length; index += 2) {
      let slide = pptx.addSlide();

      const product = products[index];
      const imageResponse = await fetch(
        `https://d1gdand68muq63.cloudfront.net/sample-products/original/${product?.images[0]?.key}`
      );
      const imageBlob = await imageResponse.blob();
      const reader = new FileReader();
      reader.readAsDataURL(imageBlob);
      const imageUrl = await new Promise<string>((resolve) => {
        reader.onloadend = () => resolve(reader.result as string);
      });
      console.log("imageUrl", imageUrl);

      slide.addImage({
        // path: imageUrl,
        data: imageUrl,
        x: 1,
        y: 1,
        w: 3,
        h: 3,
      });
      slide.addText(
        `${product.code} (${product.centimeter.length} x ${product.centimeter.width} x ${product.centimeter.height} CM)`,
        {
          x: 1,
          y: 4.5,
          fontSize: 18,
          color: "333333",
        }
      );

      if (products[index + 1]) {
        const nextProduct = products[index + 1];
        const nextImageResponse = await fetch(
          `https://d1gdand68muq63.cloudfront.net/sample-products/original/${nextProduct?.images[0]?.key}`
        );

        const nextImageBlob = await nextImageResponse.blob();
        const reader = new FileReader();
        reader.readAsDataURL(nextImageBlob);
        const nextImageUrl = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
        });

        slide.addImage({
          data: nextImageUrl,

          x: 5,
          y: 1,
          w: 3,
          h: 3,
        });
        slide.addText(
          `${nextProduct.code} (${nextProduct.centimeter.length} x ${nextProduct.centimeter.width} x ${nextProduct.centimeter.height} CM)`,
          {
            x: 5,
            y: 4.5,
            fontSize: 18,
            color: "333333",
          }
        );
      }
    }

    // Slide 3: Thank You Slide
    let slide3 = pptx.addSlide();
    slide3.background = { fill: "FFFFFF" };
    slide3.addText("THANK YOU", {
      x: 1.5,
      y: 1.5,
      fontSize: 36,
      bold: true,
      color: "000000",
      align: "center",
    });
    slide3.addText("PRINCE ART EXPORTER", {
      x: 1.5,
      y: 2.5,
      fontSize: 18,
      color: "666666",
      align: "center",
    });
    slide3.addText("F-288, MIA 2nd PHASE, BASNI, JODHPUR (RAJ.) 342005 INDIA", {
      x: 1.5,
      y: 3,
      fontSize: 14,
      color: "999999",
      align: "center",
    });

    pptx.writeFile({ fileName: "Photo_Offer_Styled" });
  };

  const handleDelete = async (user: any) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      const response = await deletePersonalizedUser(user.uuid);
      if (response.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["admin-personalized-users"],
        });
        Swal.fire("Deleted!", "The user has been deleted.", "success");
      }
    }
  };

  return (
    <section className="ec-page-content ec-vendor-dashboard section-space-p">
      <div className="container">
        <div className="row">
          <Sidebar />
          <div className="ec-shop-rightside col-lg-9 col-md-12">
            <div className="ec-vendor-dashboard-card space-bottom-30">
              <div className="ec-vendor-card-header">
                <h5>Personalized Users</h5>
                <div className="ec-header-btn">
                  <div className="btn btn-lg btn-primary" onClick={toggle}>
                    Add Product
                  </div>
                </div>
              </div>
              <div className="ec-vendor-card-body">
                <div className="ec-vendor-card-table">
                  <table className="table ec-table">
                    <thead>
                      <tr>
                        <th scope="col">Name</th>
                        <th scope="col">Total Products</th>
                        <th scope="col">Total Categories</th>
                        <th scope="col">Created Date</th>
                        <th scope="col">Expired Date</th>
                        <th scope="col">Status</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {response?.data.results.map((user: any) => (
                        <tr key={user.id}>
                          <td>
                            <span>
                              {user.userDetails.firstName}{" "}
                              {user.userDetails.lastName}
                            </span>
                          </td>
                          <td>
                            <span>{user.productCount}</span>
                          </td>
                          <td>
                            <span>{user.categoryCount}</span>
                          </td>
                          <td>
                            <span>
                              {moment(user.createdDate).format("YYYY-MM-DD")}
                            </span>
                          </td>
                          <td>
                            <span>
                              {moment(user.expiredDate).format("YYYY-MM-DD")}
                            </span>
                          </td>
                          <td>
                            <span>
                              {" "}
                              <Toggle
                                defaultChecked={user.isActive}
                                icons={false}
                                onChange={() => {
                                  console.log("toggle", user);
                                }}
                              />
                              {/* {user.isActive ? "True" : "False"} */}
                            </span>
                          </td>
                          <td>
                            <span>
                              <Link
                                to=""
                                className="ec-action-btn mr-1"
                                onClick={() => {
                                  handleDownloadPPT(user);
                                }}
                              >
                                <i className="fa fa-download fs-5"></i>
                              </Link>
                              {/* <Link
                                // to={`edit/${user.id}`}
                                to=""
                                className="ec-action-btn mr-1"
                                onClick={() => {
                                  // setCreatedDate(
                                  //   moment(user.createdDate).format(
                                  //     "YYYY-MM-DD"
                                  //   )
                                  // );
                                  // setExpireDate(
                                  //   moment(user.expiredDate).format(
                                  //     "YYYY-MM-DD"
                                  //   )
                                  // );
                                  // setSelectedUser(user);
                                  // toggle();
                                }}
                              >
                                <i className="fa fa-edit fs-5"></i>
                              </Link> */}
                              <Link
                                onClick={() => {
                                  handleDelete(user);
                                }}
                                to={""}
                                className="ec-action-btn text-danger "
                              >
                                <i className="fa fa-trash fs-5"></i>
                              </Link>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="col-lg-12">
                  <ReactPaginate
                    nextLabel=">>"
                    onPageChange={handlePageChange}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    pageCount={response?.data.totalPages}
                    forcePage={currentPage - 1}
                    previousLabel="<<"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakLabel="..."
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    containerClassName="pagination"
                    activeClassName="active"
                    renderOnZeroPageCount={null}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal fullscreen="sm" size="sm" centered isOpen={modal} toggle={toggle}>
        <ModalHeader>Select User for personalized product</ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-sm-4">
              <FormSelect
                label="User"
                name="user"
                margin="0"
                options={users}
                getOptionLabel={(option: any) =>
                  `${option.firstName} ${option.lastName} (${option?.company?.name})`
                }
                getOptionValue={(option: any) => option.id}
                onChange={(name: any, value: any) => {
                  setSelectedUser(value);
                }}
                // value={values.category}
                // error={errors.category}
                // touched={touched.category}
              />
            </div>
            <div className="col-sm-4">
              <div className="sc-bdfBQB bHsaux default-form-box">
                <label className="form-label form-label">Created Date</label>
                <input
                  name="createdDate"
                  type="date"
                  className="form-control"
                  value={createdDate}
                  disabled={true}
                  onChange={(e) => {
                    setCreatedDate(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="col-sm-4">
              <div className="sc-bdfBQB bHsaux default-form-box">
                <label className="form-label form-label">Expired Date</label>
                <input
                  name="expiredDate"
                  type="date"
                  className="form-control"
                  value={expireDate}
                  onChange={(e) => {
                    setExpireDate(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              if (selectedUser) {
                navigate(
                  `/admin/personalized-users/${selectedUser.id}/products`,
                  {
                    state: {
                      isAddProduct: true,
                    },
                  }
                );
                localStorage.setItem("createdDate", createdDate);
                localStorage.setItem("expireDate", expireDate);
              } else {
                alert("Please select a user");
              }
              toggle();
            }}
          >
            Proceed
          </Button>{" "}
          <Button
            color="secondary"
            onClick={() => {
              toggle();
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </section>
  );
}
